import React, { ReactNode } from 'react';
import { BaseErrorBoundary, BaseErrorBoundaryProps } from './BaseErrorBoundary';
import { createNotification } from '../../utils/dom';
import { trackError, ErrorSeverity, type ErrorContext } from '../../utils/error-tracking';

interface AsyncErrorBoundaryProps extends Omit<BaseErrorBoundaryProps, 'fallback'> {
  children: ReactNode;
  fallback?: ReactNode;
  onRetry?: () => void | Promise<void>;
  loadingFallback?: ReactNode;
  isLoading?: boolean;
}

interface AsyncErrorBoundaryState {
  hasError: boolean;
  isRetrying: boolean;
  error: Error | null;
}

export class AsyncErrorBoundary extends React.Component<AsyncErrorBoundaryProps, AsyncErrorBoundaryState> {
  private retryPromise: Promise<void> | null = null;

  constructor(props: AsyncErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      isRetrying: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<AsyncErrorBoundaryState> {
    // Send notification for async errors
    createNotification({
      type: 'error',
      msg: 'Failed to load content. Please try again.',
      dismissed: false,
    });

    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Track error with component context
    const context: ErrorContext = {
      component: 'AsyncErrorBoundary',
      boundary: 'async',
      componentStack: errorInfo.componentStack,
      tags: {
        errorBoundary: 'async',
        hasOnRetry: String(!!this.props.onRetry),
      },
    };

    trackError(error, ErrorSeverity.ERROR, context);
  }

  handleAsyncRetry = async () => {
    const { onRetry } = this.props;

    this.setState({ isRetrying: true });

    // Show retrying notification
    createNotification({
      type: 'info',
      msg: 'Retrying to load content...',
      dismissed: false,
    });

    try {
      if (onRetry) {
        this.retryPromise = Promise.resolve(onRetry());
        await this.retryPromise;
      }

      // Reset error state after successful retry
      this.setState({
        hasError: false,
        isRetrying: false,
        error: null,
      });

      // Show success notification
      createNotification({
        type: 'success',
        msg: 'Content loaded successfully!',
        dismissed: false,
      });
    } catch (retryError) {
      trackError(retryError as Error, ErrorSeverity.ERROR, { component: 'AsyncErrorBoundary', boundary: 'async-retry' });

      // Show failure notification
      createNotification({
        type: 'error',
        msg: 'Failed to load content after retry. Please refresh the page.',
        dismissed: false,
      });

      this.setState({
        isRetrying: false,
        error: retryError as Error,
      });
    } finally {
      this.retryPromise = null;
    }
  };

  componentWillUnmount() {
    // Cancel any pending retry
    this.retryPromise = null;
  }

  renderAsyncErrorFallback = (error: Error) => {
    const { loadingFallback } = this.props;
    const { isRetrying } = this.state;

    if (isRetrying && loadingFallback) {
      return loadingFallback;
    }

    if (isRetrying) {
      return (
        <div className="flex flex-col items-center justify-center p-8 min-h-[200px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
          <p className="text-gray-600">Retrying...</p>
        </div>
      );
    }

    // No `fallback` branch: with a fallback the inner BaseErrorBoundary shows
    // it and never rethrows, so this boundary only catches when there is none.

    return (
      <div className="flex flex-col items-center justify-center p-8 bg-yellow-50 rounded-lg border border-yellow-200">
        <div className="text-center max-w-md">
          <div className="mb-4">
            <svg
              className="mx-auto h-12 w-12 text-yellow-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Failed to load content
          </h3>

          <p className="text-gray-600 mb-4">
            {error.message || 'The content could not be loaded. Please try again.'}
          </p>

          <button
            onClick={this.handleAsyncRetry}
            disabled={isRetrying}
            className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Retry loading
          </button>
        </div>
      </div>
    );
  };

  render() {
    const { hasError, error } = this.state;
    const { isLoading, loadingFallback, children, ...baseProps } = this.props;

    if (isLoading && loadingFallback) {
      return loadingFallback;
    }

    if (hasError && error) {
      return (
        <BaseErrorBoundary
          {...baseProps}
          fallback={this.renderAsyncErrorFallback(error)}
          enableRetry={true}
          maxRetries={3}
        >
          {children}
        </BaseErrorBoundary>
      );
    }

    return (
      <BaseErrorBoundary {...baseProps} enableRetry={true} maxRetries={3}>
        {children}
      </BaseErrorBoundary>
    );
  }
}

// Hook for triggering async errors manually
export const useAsyncError = () => {
  const [, setError] = React.useState();

  return React.useCallback(
    (error: Error) => {
      setError(() => {
        throw error;
      });
    },
    [setError]
  );
};

// Wrapper for async operations with error boundary
export const AsyncBoundary: React.FC<AsyncErrorBoundaryProps> = (props) => {
  return <AsyncErrorBoundary {...props} />;
};

export default AsyncErrorBoundary;